import { useAuth } from "@clerk/clerk-react";
import { OrganizationUserContext } from "contexts/Organization";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { EmailSettings } from "schemas/email";
import { SendOutreachApiRouteResponse } from "schemas/outreach";

import OutreachSendSnackbar from "components/OutreachSendSnackbar";
import { fetcherAuth } from "utils/api";

export const UNDO_WINDOW_MS = 5000;

export type OutreachSendStatus =
  | "sending"
  | "processing"
  | "sent"
  | "failed"
  | "undone";

export interface OutreachSendState {
  status: OutreachSendStatus;
  contactEmail: string;
  isScheduled: boolean;
  errorMessage?: string;
  outreachContactId?: number;
}

export interface EnqueueSendOptions {
  contactEmail: string;
  payload: Record<string, unknown>;
  isScheduled: boolean;
  // Applied to OrganizationUserContext immediately on enqueue so subsequent
  // brand views see the post-send EmailSettings without waiting for the
  // ~5s undo window and the API round-trip to finish. The real response
  // reconciles via onSuccess.
  optimisticEmailSettings?: EmailSettings;
  onSuccess?: (res: SendOutreachApiRouteResponse) => void;
  onError?: (error: Error) => void;
  onUndo?: () => void;
}

interface OutreachSendContextInterface {
  state: OutreachSendState | null;
  enqueueSend: (options: EnqueueSendOptions) => void;
  undo: () => void;
  dismiss: () => void;
}

const defaultContextMissingFunction = () => {
  throw new Error("OutreachSendContext is missing");
};

const OutreachSendContext = createContext<OutreachSendContextInterface>({
  state: null,
  enqueueSend: defaultContextMissingFunction,
  undo: () => {},
  dismiss: () => {},
});

interface OutreachSendProviderProps {
  children: ReactNode;
}

interface PendingUndo {
  options: EnqueueSendOptions;
  sendId: number;
}

const OutreachSendProvider = ({ children }: OutreachSendProviderProps) => {
  const { getToken } = useAuth();
  const { currentOrg, setEmailSettings } = useContext(OrganizationUserContext);

  const [state, setState] = useState<OutreachSendState | null>(null);

  // Latest enqueued send "owns" the snackbar; older sends still fire their API
  // but don't update visible state, so concurrent sends don't clobber each other.
  const sendIdCounter = useRef(0);
  const currentSnackbarSendId = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUndoRef = useRef<PendingUndo | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearTimer();
  }, []);

  const performApiCall = useCallback(
    async (options: EnqueueSendOptions, sendId: number) => {
      if (!currentOrg?.id) return;

      const ownsSnackbar = () => currentSnackbarSendId.current === sendId;

      if (ownsSnackbar()) {
        setState({
          status: "processing",
          contactEmail: options.contactEmail,
          isScheduled: options.isScheduled,
        });
      }

      try {
        const res: SendOutreachApiRouteResponse = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/emails/send-outreach`,
          "POST",
          {},
          options.payload,
        );
        if (ownsSnackbar()) {
          setState({
            status: "sent",
            contactEmail: options.contactEmail,
            isScheduled: options.isScheduled,
            outreachContactId: res?.outreachContactId,
          });
        }
        options.onSuccess?.(res);
      } catch (err) {
        const error = err as Error;
        if (ownsSnackbar()) {
          setState({
            status: "failed",
            contactEmail: options.contactEmail,
            isScheduled: options.isScheduled,
            errorMessage: error?.message,
          });
        }
        options.onError?.(error);
      }
    },
    [currentOrg?.id, getToken],
  );

  const enqueueSend = useCallback(
    (options: EnqueueSendOptions) => {
      // A previous send is still in its undo window — commit it now so it
      // doesn't get dropped. Its state updates are silenced via sendId.
      const previous = pendingUndoRef.current;
      if (previous) {
        pendingUndoRef.current = null;
        clearTimer();
        performApiCall(previous.options, previous.sendId);
      }

      if (options.optimisticEmailSettings) {
        setEmailSettings(options.optimisticEmailSettings);
      }

      sendIdCounter.current += 1;
      const sendId = sendIdCounter.current;
      currentSnackbarSendId.current = sendId;

      if (options.isScheduled) {
        performApiCall(options, sendId);
        return;
      }

      pendingUndoRef.current = { options, sendId };
      setState({
        status: "sending",
        contactEmail: options.contactEmail,
        isScheduled: false,
      });
      timerRef.current = setTimeout(() => {
        pendingUndoRef.current = null;
        timerRef.current = null;
        performApiCall(options, sendId);
      }, UNDO_WINDOW_MS);
    },
    [performApiCall, setEmailSettings],
  );

  const undo = useCallback(() => {
    const previous = pendingUndoRef.current;
    pendingUndoRef.current = null;
    clearTimer();
    setState((prev) =>
      prev
        ? {
            status: "undone",
            contactEmail: prev.contactEmail,
            isScheduled: prev.isScheduled,
          }
        : prev,
    );
    previous?.options.onUndo?.();
  }, []);

  const dismiss = useCallback(() => {
    setState(null);
  }, []);

  return (
    <OutreachSendContext.Provider value={{ state, enqueueSend, undo, dismiss }}>
      {children}
      <OutreachSendSnackbar />
    </OutreachSendContext.Provider>
  );
};

export { OutreachSendContext, OutreachSendProvider };
